// ResourcesSection.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const ResourcesSection = () => {
  const [activeResource, setActiveResource] = useState(0);

  const resources = [
    {
      title: 'Study Abroad Pathways',
      description: 'Our study abroad resources are designed to support students at every stage of the admissions process. From understanding entry requirements to preparing strong personal statements and tracking applications, we provide clear guidance to help you navigate international education systems with confidence.\n\nThese resources are made available through our client portal once you complete your consultation and onboarding process.'
    },
    {
      title: 'Work Abroad Opportunities',
      description: 'Our work abroad resources focus on helping candidates prepare for international employment. This includes role expectations, employer insights, interview preparation materials, and guidance on documentation requirements to ensure you are fully prepared when opportunities arise.'
    },
    {
      title: 'Consultation & Eligibility Tools',
      description: 'We provide structured tools and checklists to help assess eligibility, clarify requirements, and outline realistic next steps. These tools ensure you understand your options clearly before committing to any pathway.'
    },
    {
      title: 'Preparation & Settlement Guides',
      description: 'Beyond applications and interviews, we offer practical guidance to help you prepare for life abroad — including accommodation considerations, cultural orientation, and understanding local systems relevant to your destination.'
    }
  ];

  return (
    <section className="py-16 bg-[#202356]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Side - Value Statement */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm text-gray-300 font-light mb-3">Our Agency Value</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                Invaluable Resources
              </h2>
              <p className="text-gray-300 text-sm font-light leading-relaxed mb-6">
                At Notapaths, we go beyond basic advice. We provide carefully curated resources, structured guidance, and ongoing support designed to help you make informed decisions before and after you take your next step abroad.
              </p>
              <p className="text-gray-300 text-sm font-light leading-relaxed mb-8">
                From preparation to placement, our resources are built to support clarity, readiness, and confidence throughout your journey.
              </p>
            </motion.div>
          </div>

          {/* Right Side - Interactive Panel */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#2542D4] rounded-lg overflow-hidden"
            >
              <div className="grid md:grid-cols-5">
                {/* Sidebar List */}
                <div className="md:col-span-2 bg-[#1a2d8f] p-6">
                  {resources.map((resource, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveResource(index)}
                      className={`w-full text-left py-4 px-4 mb-2 rounded transition-colors ${
                        activeResource === index
                          ? 'bg-[#2542D4] text-white'
                          : 'text-gray-300 hover:bg-[#2542D4]/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-normal ${
                          activeResource === index ? 'bg-white text-[#2542D4]' : 'bg-[#2542D4] text-white'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="text-sm font-normal">{resource.title}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Main Content Panel */}
                <div className="md:col-span-3 p-8">
                  <motion.div
                    key={activeResource}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 className="text-2xl font-semibold text-white mb-6">
                      {resources[activeResource].title}
                    </h3>
                    <p className="text-gray-200 text-sm font-light leading-relaxed whitespace-pre-line">
                      {resources[activeResource].description}
                    </p>
                    <button className="mt-8 w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition">
                      <ArrowRight size={20} className="text-[#2542D4]" />
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourcesSection;
